<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { nanoid } from 'nanoid'
import AIDisclaimer from '@/components/AIDisclaimer.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import CharacteristicsForm from './CharacteristicsForm.vue'
import GeneratingMessage from './GeneratingMessage.vue'
import SDIStrategiesResults from './SDIStrategiesResults.vue'
import { generateSdiStrategies, getSdiStudentCharacteristics } from './api'
import { SDI_STRATEGIES_EVENT_NAMES } from './event_names'
import { sdiGradeLevel } from './grade_level'
import type { GeneratedStrategies, StudentCharacteristic } from './sdi_types'

const props = withDefaults(defineProps<{
  goalText: string
  goalGrades: string[]
  isIndented?: boolean
}>(), {
  isIndented: false,
})

const GENERATION_ERROR_MESSAGE = 'There was an error generating your strategies.'

const characteristicOptions = ref<StudentCharacteristic[]>([])
const selectedCharacteristics = ref<StudentCharacteristic[]>([])
const generatedStrategies = ref<GeneratedStrategies | null>(null)
const isGenerating = ref(false)
const generationError = ref('')
const traceId = ref('')

const isShowingGeneratingMessage = computed(() => isGenerating.value || Boolean(generationError.value))

// Vue 2 sends an empty string, not an empty list, when nothing is selected.
const selectedCharacteristicNames = computed(() => {
  if (selectedCharacteristics.value.length === 0) {
    return ''
  }
  return selectedCharacteristics.value.map((characteristic) => characteristic.name)
})

const analytics = getAnalyticsTracker().getChildContext({
  sdi_characteristics: selectedCharacteristicNames,
})

async function fetchCharacteristics() {
  try {
    characteristicOptions.value = await getSdiStudentCharacteristics()
  } catch (error) {
    console.error(error)
  }
}

function selectedCharacteristicsForRequest(): StudentCharacteristic[] {
  return selectedCharacteristics.value.map(({ id, name }) => ({ name, id }))
}

const strategiesContainer = ref<HTMLElement | null>(null)
const resultsRegion = ref<HTMLElement | null>(null)

async function focusResults() {
  await nextTick()
  resultsRegion.value?.focus()
}

async function focusGenerateButton() {
  await nextTick()
  strategiesContainer.value?.querySelector<HTMLElement>('[data-testid="generate-sdi-strategies"]')?.focus()
}

async function generateStrategies() {
  if (isGenerating.value) {
    return
  }
  generatedStrategies.value = null
  generationError.value = ''
  traceId.value = nanoid()
  isGenerating.value = true
  analytics.sendEvent(SDI_STRATEGIES_EVENT_NAMES.CLICKED_GENERATE, { sdi_trace: traceId.value })
  try {
    generatedStrategies.value = await generateSdiStrategies({
      goal_text: props.goalText,
      grade_level: sdiGradeLevel(props.goalGrades),
      characteristics: selectedCharacteristicsForRequest(),
      sdi_trace: traceId.value,
      anchor_page_url: window.location.href,
    })
  } catch (error) {
    generationError.value = GENERATION_ERROR_MESSAGE
    console.error('Error generating SDI strategies: ', error)
  }
  isGenerating.value = false
  if (generatedStrategies.value) {
    await focusResults()
  }
}

function clearSelections() {
  analytics.sendEvent(SDI_STRATEGIES_EVENT_NAMES.CLICKED_CLEARED, { sdi_characteristics: selectedCharacteristicNames.value })
  selectedCharacteristics.value = []
}

function startOver() {
  generatedStrategies.value = null
  analytics.sendEvent(SDI_STRATEGIES_EVENT_NAMES.CLICKED_START_OVER, { sdi_characteristics: selectedCharacteristicNames.value })
  selectedCharacteristics.value = []
  focusGenerateButton()
}

function cancelAfterError() {
  generationError.value = ''
  focusGenerateButton()
}

function trackDisclaimerOpened() {
  analytics.sendEvent(SDI_STRATEGIES_EVENT_NAMES.OPENED_AI_DISCLAIMER)
}

onMounted(fetchCharacteristics)
</script>

<template>
  <div ref="strategiesContainer" class="relative text-lg">
    <div :class="{ 'px-12 pb-4': isIndented }" :inert="isShowingGeneratingMessage">
      <p class="mb-4">Recommend UDL-aligned instructional strategies for implementing this goal based on the following information about my student:</p>
      <div ref="resultsRegion" tabindex="-1" class="focus:outline-none" aria-live="polite" data-testid="sdi-results-region">
        <SDIStrategiesResults
          v-if="generatedStrategies"
          :generated-strategies="generatedStrategies"
          :selected-characteristics="selectedCharacteristics"
          :trace-id="traceId"
          @reset="startOver"
        />
      </div>
      <CharacteristicsForm
        v-if="!generatedStrategies"
        v-model="selectedCharacteristics"
        :characteristic-options="characteristicOptions"
        @submit="generateStrategies"
        @clear-selections="clearSelections"
      />
      <AIDisclaimer class="mt-3" :is-beta="false" @opened="trackDisclaimerOpened" />
    </div>
    <GeneratingMessage
      v-if="isShowingGeneratingMessage"
      :generation-error="generationError"
      @retry="generateStrategies"
      @cancel="cancelAfterError"
    />
  </div>
</template>
